// ProyectoDetailInstructorList.jsx
import React, { Component } from "react";
import { Button, Table, Alert, Row, Col, Spinner } from "reactstrap";
import Moment from "moment";
import ProyectoIngresoModal from "./ProyectoIngresoModal";
import ConfirmInstructorRemovalModal from "./ConfirmInstructorRemovalModal";
import emailjs from "@emailjs/browser";
import { getPartners, instructor_emails } from "../../../api-services";
import { toast } from "react-toastify";
import Papa from 'papaparse';
import * as XLSX from "xlsx";
import axios from "axios";
import { API_URL_instructores } from "../../../constants";

import { getAuthHeaders } from "../../../utils/auth";
// ---- helpers ----
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (v) =>
  num(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Prefer API origin for confirmation links
const API_BASE =
  process.env.REACT_APP_API_ORIGIN ||
  process.env.VITE_API_ORIGIN ||
  process.env.NEXT_PUBLIC_API_ORIGIN ||
  window.location.origin;

class ProyectoDetailInstructorList extends Component {
  state = {
    currentPage: 1,
    searchQuery: "",
    filteredData: [],
    loadingEmail: false,
    file: null,
    isUploading: false,
  };

  componentDidMount() {
    this.fetchPartnersAndInstructors();
  }

  fetchPartnersAndInstructors = async () => {
    const headers = getAuthHeaders();
    const { selectedProyecto } = this.props;
    const proyectoCode =
      selectedProyecto?.proyecto_code || selectedProyecto?.proyectoCode;

    if (!proyectoCode) {
      toast.error("Falta proyecto_code.");
      return [];
    }

    // 1) Instructors filtered by proyecto_code
    const instructors = await instructor_emails(headers, proyectoCode);

    // 2) Partners (optionally filtered by proyecto_code on BE)
    const partners = await getPartners(headers, proyectoCode);

    // Map partner name -> email
    const emailByPartner = {};
    for (const p of partners || []) {
      if (p?.partner) emailByPartner[p.partner] = p.email || "";
    }

    // 3) Merge list for emailing
    const merged = (instructors || []).map((inst) => {
      const partnerName = inst?.instructor || "";
      const email = emailByPartner[partnerName] || inst?.email || "";
      return { partner: partnerName, email, instructor: inst };
    });

    this.setState({ filteredData: merged });
    return merged;
  };

  /**
   * Determines if an instructor should receive an email
   */
  shouldSendEmail = (instructor) => {
    if (!instructor) return false;

    const status = (instructor.confirmation_status || "pending").toLowerCase();
    
    // Don't send if already confirmed or declined
    if (status === "confirmed") {
      return false;
    }
    
    if (status === "declined") {
      return false;
    }

    // Don't send if missing token
    if (!instructor.confirm_token) {
      return false;
    }

    return true;
  };

  /**
   * Get counts for email sending summary
   */
  getEmailSendSummary = (instructors) => {
    let pending = 0;
    let confirmed = 0;
    let declined = 0;
    let noEmail = 0;
    let noToken = 0;

    for (const item of instructors || []) {
      const inst = item?.instructor || {};
      const email = (item?.email || "").trim();
      const status = (inst.confirmation_status || "pending").toLowerCase();

      if (!email) {
        noEmail++;
        continue;
      }

      if (!inst.confirm_token) {
        noToken++;
        continue;
      }

      if (status === "confirmed") {
        confirmed++;
      } else if (status === "declined") {
        declined++;
      } else {
        pending++;
      }
    }

    return { pending, confirmed, declined, noEmail, noToken };
  };

  sendEmail = async (e) => {
    e.preventDefault();
    this.setState({ loadingEmail: true });

    const { proyectoNameSelected, selectedProyecto } = this.props;
    const proyecto = selectedProyecto || {};

    const toastId = toast.loading("Enviando correos…");
    let sent = 0;
    let failed = 0;
    let skippedAlreadyConfirmed = 0;
    let skippedAlreadyDeclined = 0;
    let skippedNoEmail = 0;
    let skippedNoToken = 0;

    try {
      const rows = (await this.fetchPartnersAndInstructors()) || [];

      // Show summary before sending
      const summary = this.getEmailSendSummary(rows);
      console.log("📧 Email sending summary:", summary);

      // Group instructors by partner email to send only ONE email per partner
      const instructorsByEmail = new Map();

      for (const item of rows) {
        const inst = item?.instructor || {};
        const email = (item?.email || "").trim();
        const status = (inst.confirmation_status || "pending").toLowerCase();

        // Skip if no email
        if (!email) {
          skippedNoEmail++;
          console.log(`⚠️ Skipped ${inst.instructor || "unknown"}: No email`);
          continue;
        }

        // Skip if no token
        if (!inst.confirm_token) {
          skippedNoToken++;
          console.log(`⚠️ Skipped ${inst.instructor || "unknown"}: No token`);
          continue;
        }

        // Skip if already confirmed
        if (status === "confirmed") {
          skippedAlreadyConfirmed++;
          console.log(`✓ Skipped ${inst.instructor}: Already confirmed`);
          continue;
        }

        // Skip if already declined
        if (status === "declined") {
          skippedAlreadyDeclined++;
          console.log(`✗ Skipped ${inst.instructor}: Already declined`);
          continue;
        }

        // Group pending instructors by email
        if (!instructorsByEmail.has(email)) {
          instructorsByEmail.set(email, {
            email,
            partner: item?.partner || "",
            instructors: []
          });
        }

        instructorsByEmail.get(email).instructors.push({
          inst,
          item,
          horas: num(inst.horas),
          semanas: num(inst.semanas),
          costoHora: num(inst.costo_hora),
          tarifaHora: num(inst.tarifa_hora ?? 0),
        });
      }

      // Send ONE email per partner with all their instructors
      for (const [email, data] of instructorsByEmail.entries()) {
        const { partner, instructors } = data;

        console.log(`📤 Sending consolidated email to ${partner} (${email}) with ${instructors.length} instructor(s)`);

        // Use the first instructor's token for confirmation links
        const firstInst = instructors[0].inst;

        // Build instructor list for email (text version for plain text emails)
        const instructorList = instructors.map(({ inst, horas, semanas, costoHora, tarifaHora }) => {
          const costoInstructor = costoHora * horas * semanas;
          const ingresoInstructor = tarifaHora * horas * semanas;

          return `
• ${inst.instructor}
  - Horas/Semana: ${horas}
  - Semanas: ${semanas}
  - Pago/Hora: ${fmt2(costoHora)}
  - Costo Total: ${fmt2(costoInstructor)}
          `.trim();
        }).join('\n\n');

        // Build HTML version for multiple instructors
        let instructorDetailsHtml = '';
        if (instructors.length > 1) {
          const moneda = proyecto?.moneda || "USD";
          instructorDetailsHtml = `
            <tr>
              <td colspan="2" style="padding:12px 14px; background:#fef3c7; border-bottom:2px solid #f59e0b;">
                <strong style="color:#92400e; font-size:14px;">⚠️ Nota:</strong>
                <span style="color:#78350f; margin-left:8px;">Se te han asignado <strong>${instructors.length} consultores</strong> en este proyecto:</span>
              </td>
            </tr>
            ${instructors.map(({ inst, horas, semanas, costoHora, tarifaHora }, index) => {
              const costoInstructor = costoHora * horas * semanas;
              const ingresoInstructor = tarifaHora * horas * semanas;
              const bgColor = index % 2 === 0 ? '#f9fafb' : '#ffffff';

              return `
            <tr>
              <td colspan="2" style="padding:16px 14px; background:${bgColor}; border-bottom:1px solid #e5e7eb;">
                <div style="margin-bottom:8px;">
                  <strong style="color:#111827; font-size:15px;">👤 ${inst.instructor}</strong>
                </div>
                <table style="width:100%; border-collapse:collapse; margin-top:8px;">
                  <tr>
                    <td style="padding:4px 0; color:#6b7280; font-size:13px; width:50%;">
                      <strong>Horas/Semana:</strong> ${horas}
                    </td>
                    <td style="padding:4px 0; color:#6b7280; font-size:13px;">
                      <strong>Semanas:</strong> ${semanas}
                    </td>
                  </tr>
                  <tr>
                    <td style="padding:4px 0; color:#6b7280; font-size:13px;">
                      <strong>Pago/Hora:</strong> ${moneda} ${fmt2(costoHora)}
                    </td>
                    <td style="padding:4px 0; color:#6b7280; font-size:13px;">
                      <strong>Tarifa/Hora:</strong> ${moneda} ${fmt2(tarifaHora)}
                    </td>
                  </tr>
                  <tr>
                    <td style="padding:4px 0; color:#059669; font-size:13px;">
                      <strong>Costo Total:</strong> ${moneda} ${fmt2(costoInstructor)}
                    </td>
                    <td style="padding:4px 0; color:#059669; font-size:13px;">
                      <strong>Ingreso Total:</strong> ${moneda} ${fmt2(ingresoInstructor)}
                    </td>
                  </tr>
                </table>
                ${inst.observaciones ? `
                <div style="margin-top:8px; padding:8px; background:#fef3c7; border-left:3px solid #f59e0b; font-size:12px; color:#78350f;">
                  <strong>Observaciones:</strong> ${inst.observaciones}
                </div>
                ` : ''}
              </td>
            </tr>
              `.trim();
            }).join('')}
            <tr>
              <td colspan="2" style="padding:12px 14px; background:#dcfce7; border-top:2px solid #22c55e;">
                <strong style="color:#166534; font-size:14px;">📊 Totales Consolidados:</strong>
                <div style="margin-top:8px; color:#166534; font-size:13px;">
                  <div><strong>Horas Totales:</strong> ${instructors.reduce((sum, i) => sum + i.horas, 0)} horas/semana</div>
                  <div><strong>Costo Total:</strong> ${moneda} ${fmt2(instructors.reduce((sum, i) => sum + (i.costoHora * i.horas * i.semanas), 0))}</div>
                  <div><strong>Ingreso Total:</strong> ${moneda} ${fmt2(instructors.reduce((sum, i) => sum + (i.tarifaHora * i.horas * i.semanas), 0))}</div>
                </div>
              </td>
            </tr>
          `.trim();
        }

        // Calculate totals across all instructors for this partner
        const totalHoras = instructors.reduce((sum, i) => sum + i.horas, 0);
        const totalCosto = instructors.reduce((sum, i) => sum + (i.costoHora * i.horas * i.semanas), 0);
        const totalIngreso = instructors.reduce((sum, i) => sum + (i.tarifaHora * i.horas * i.semanas), 0);

        // Confirmation links with email parameter
        const emailParam = `&email=${encodeURIComponent(email)}`;
        const confirmUrl = `${API_BASE}/api/instructor-confirm/${firstInst.confirm_token}/?action=confirm&redirect=1${emailParam}`;
        const declineUrl = `${API_BASE}/api/instructor-confirm/${firstInst.confirm_token}/?action=decline&redirect=1${emailParam}`;

        // Email template params
        const emailParams = {
          to_email: email,
          from_name: "LSSI Fin",
          to_name: partner,
          reply_to: "bgonzalez@leansixsigmainstitute.org",

          // Multiple instructors info
          instructor: instructors.length > 1
            ? `${instructors.length} instructores asignados`
            : instructors[0].inst.instructor,
          instructor_list: instructorList,
          instructor_details_html: instructorDetailsHtml,
          num_instructors: instructors.length,
          miembro_que_asigna: firstInst?.miembro_que_asigna || "",
          horas: totalHoras,
          semanas: instructors[0].semanas, // Assuming same duration
          moneda: proyecto?.moneda || "USD",
          observaciones: instructors[0].inst?.observaciones || "",

          // Proyecto
          proyectoNameFrom: proyectoNameSelected || "",
          cliente: proyecto?.cliente || "",
          producto: proyecto?.producto || "",
          fecha_inicio: proyecto?.inicio_date
            ? Moment(proyecto.inicio_date).format("MMM/DD/yy")
            : "",
          fecha_final: proyecto?.terminacion_date
            ? Moment(proyecto.terminacion_date).format("MMM/DD/yy")
            : "",
          modalidad: proyecto?.modalidad || "",
          lider: proyecto?.lider || "",

          // Totals
          pago_hora: instructors.length > 1 ? "Variable" : fmt2(instructors[0].costoHora),
          tarifa_hora: instructors.length > 1 ? "Variable" : fmt2(instructors[0].tarifaHora),
          costo_instructor: fmt2(totalCosto),
          ingreso_instructor: fmt2(totalIngreso),

          // Confirmation links
          confirm_url: confirmUrl,
          decline_url: declineUrl,

          // Extras
          instructor_email: email,
          proyecto_code: firstInst?.proyecto_code || proyecto?.proyecto_code || "",
        };

        try {
          await emailjs.send(
            "service_avk65uk",
            "template_he5jinj",
            emailParams,
            "VBYWnQsgbxRBcGQ8N"
          );
          sent++;
          console.log(`✅ Email sent successfully to ${partner} with ${instructors.length} instructor(s)`);
        } catch (err) {
          console.error(`❌ Error sending email to ${partner}:`, err);
          failed++;
        }
      }

      // Build result message
      const parts = [];
      if (sent > 0) parts.push(`✓ ${sent} correo${sent > 1 ? "s" : ""} enviado${sent > 1 ? "s" : ""}`);
      if (failed > 0) parts.push(`✗ ${failed} fallido${failed > 1 ? "s" : ""}`);
      if (skippedAlreadyConfirmed > 0)
        parts.push(`✓ ${skippedAlreadyConfirmed} ya confirmado${skippedAlreadyConfirmed > 1 ? "s" : ""}`);
      if (skippedAlreadyDeclined > 0)
        parts.push(`✗ ${skippedAlreadyDeclined} ya rechazado${skippedAlreadyDeclined > 1 ? "s" : ""}`);
      if (skippedNoEmail > 0)
        parts.push(`⚠ ${skippedNoEmail} sin email`);
      if (skippedNoToken > 0)
        parts.push(`⚠ ${skippedNoToken} sin token`);

      const message = parts.length > 0 ? parts.join(", ") : "No se enviaron correos";

      toast.update(toastId, {
        render: message,
        type: sent > 0 && failed === 0 ? "success" : failed > 0 ? "warning" : "info",
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });

      console.log("📊 Final summary:", {
        sent,
        failed,
        skippedAlreadyConfirmed,
        skippedAlreadyDeclined,
        skippedNoEmail,
        skippedNoToken,
      });
    } catch (err) {
      console.error("Error preparing email list:", err);
      toast.update(toastId, {
        render: "No se pudo preparar la lista de correos.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      this.setState({ loadingEmail: false });
    }
  };

  /**
   * Get summary of instructors by status for display
   */
  getInstructorStatusSummary = () => {
    const { instructores } = this.props;
    let pending = 0;
    let confirmed = 0;
    let declined = 0;

    for (const inst of instructores || []) {
      const status = (inst.confirmation_status || "pending").toLowerCase();
      if (status === "confirmed") confirmed++;
      else if (status === "declined") declined++;
      else pending++;
    }

    return { pending, confirmed, declined, total: instructores?.length || 0 };
  };

  // Excel Download functionality
  downloadExcel = () => {
    const { instructores, proyectoCodeSelected, proyectoNameSelected, selectedProyecto } = this.props;

    if (!instructores || instructores.length === 0) {
      toast.info("No hay instructores para exportar.");
      return;
    }

    // Prepare data rows with complete instructor information
    const rows = instructores.map(inst => ({
      'Proyecto Code': proyectoCodeSelected || inst.proyecto_code || '—',
      'Proyecto': proyectoNameSelected || '—',
      'Instructor': inst.instructor_name || inst.instructor || '—',
      'Email': inst.email || '—',
      'Partner que Asigna': inst.miembro_que_asigna || '—',
      'Modalidad': inst.modalidad || '—',
      'Horas/Semana': Number((inst.horas || 0).toFixed(2)),
      'Precio/Hora': Number((inst.tarifa_hora || 0).toFixed(2)),
      'Costo/Hora': Number((inst.costo_hora || 0).toFixed(2)),
      'Número de Semanas': Number((inst.semanas || 0).toFixed(2)),
      'Horas/Producto Total': Number((inst.product || 0).toFixed(2)),
      'Ingreso Total': Number((inst.ingreso || 0).toFixed(2)),
      'Costo Total': Number((inst.costo || 0).toFixed(2)),
      'Utilidad': Number(((inst.ingreso || 0) - (inst.costo || 0)).toFixed(2)),
      'Moneda': inst.moneda || selectedProyecto?.moneda || 'USD',
      'Observaciones': inst.observaciones || '',
      'Estado Confirmación': inst.confirmation_status || 'pending',
    }));

    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto-size columns
    ws['!cols'] = [
      { wch: 15 },  // Proyecto Code
      { wch: 30 },  // Proyecto
      { wch: 25 },  // Instructor
      { wch: 30 },  // Email
      { wch: 20 },  // Partner que Asigna
      { wch: 12 },  // Modalidad
      { wch: 12 },  // Horas/Semana
      { wch: 12 },  // Precio/Hora
      { wch: 12 },  // Costo/Hora
      { wch: 15 },  // Número de Semanas
      { wch: 18 },  // Horas/Producto Total
      { wch: 15 },  // Ingreso Total
      { wch: 15 },  // Costo Total
      { wch: 12 },  // Utilidad
      { wch: 10 },  // Moneda
      { wch: 40 },  // Observaciones
      { wch: 18 },  // Estado Confirmación
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Instructores');

    // Download
    const today = new Date();
    const dateStr = today.toISOString().slice(0, 10);
    const fileName = `Instructores_${proyectoCodeSelected || 'proyecto'}_${dateStr}.xlsx`;

    XLSX.writeFile(wb, fileName);
    toast.success("📥 Excel descargado exitosamente.");
  };

  handleFileChange = (event) => {
    this.setState({ file: event.target.files[0] });
  };

  handleFileUpload = () => {
    const { file } = this.state;
    if (!file) {
      toast.warning('Por favor selecciona un archivo');
      return;
    }

    console.log('📁 File selected:', file.name, 'Type:', file.type);
    this.setState({ isUploading: true });

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: "UTF-8",
      delimiter: ";",
      complete: this.updateDatabase,
      error: (error) => {
        console.error('❌ Error parsing file:', error);
        toast.error("Error al procesar el archivo. Por favor intenta de nuevo.");
        this.setState({ isUploading: false, file: null });
      }
    });
  };

  updateDatabase = async (result) => {
    console.log('📊 Raw CSV parsing result:', result);
    console.log('📝 First row raw data:', result.data[0]);

    const normalized = result.data.map(row => {
      const cleaned = {};
      Object.keys(row).forEach(key => {
        if (!key) return;
        const newKey = key.trim();
        cleaned[newKey] = row[key];
      });
      return cleaned;
    });

    console.log('🧹 Normalized data (first row):', normalized[0]);

    const { proyectoCodeSelected, selectedProyecto } = this.props;
    const proyectoMoneda = selectedProyecto?.moneda || 'USD';

    const data = normalized
      .filter(row => row.Instructor) // skip empty rows
      .map(row => ({
        proyecto_code: proyectoCodeSelected || (row["Proyecto Code"] || "").trim(),
        instructor: (row.Instructor || "").trim(),
        miembro_que_asigna: (row["Partner que Asigna"] || "").trim(),
        horas: parseFloat((row["Horas x Semana"] || "0").trim()),
        tarifa_hora: parseFloat((row["Precio x Hora"] || "0").trim()),
        costo_hora: parseFloat((row["Costo x Hora"] || "0").trim()),
        moneda: proyectoMoneda, // Inherited from project
        semanas: parseFloat((row["Numero de Semanas"] || "0").trim()),
        observaciones: (row.Observaciones || "").trim(),
      }));

    console.log('🎯 Final data to send (first entry):', data[0]);

    if (data.length === 0) {
      toast.warning("No se encontraron datos válidos en el archivo");
      this.setState({ isUploading: false, file: null });
      return;
    }

    const headers = getAuthHeaders();

    console.log('📤 Sending to API with headers:', headers);

    try {
      const promises = data.map(entry =>
        axios.post(API_URL_instructores, entry, { headers })
          .then(() => ({ success: true, entry }))
          .catch(error => ({ success: false, entry, error }))
      );

      const results = await Promise.all(promises);

      const successes = results.filter(r => r.success).length;
      const failures = results.filter(r => !r.success);

      console.log('✅ Successes:', successes, '❌ Failures:', failures.length);

      if (failures.length === 0) {
        toast.success(`${successes} instructor(es) subido(s) exitosamente`);
      } else if (successes > 0) {
        toast.warning(`${successes} instructor(es) subido(s), ${failures.length} fallaron`);
        console.error('Failed entries:', failures);
      } else {
        toast.error(`Error al subir instructores. Por favor intenta de nuevo.`);
        console.error('All entries failed:', failures);
      }

      if (successes > 0 && this.props.resetState) {
        this.props.resetState();
      }

      this.setState({ file: null, isUploading: false });

    } catch (error) {
      toast.error("Error al actualizar la base de datos. Por favor intenta de nuevo.");
      console.error('❌ Error updating database:', error);

      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data);
      }

      this.setState({ isUploading: false, file: null });
    }
  };

  render() {
    const { instructores } = this.props;
    const { loadingEmail, isUploading } = this.state;

    const statusSummary = this.getInstructorStatusSummary();
    const hasInstructors = instructores && instructores.length > 0;
    const hasPendingInstructors = statusSummary.pending > 0;

    return (
      <div>
        {/* CSV Download/Upload Section */}
        <Row className="mb-3">
          <Col>
            <Button
              style={{ backgroundColor: '#ADD8E6', color: 'black' }}
              onClick={this.downloadExcel}
              disabled={isUploading || !hasInstructors}
            >
              📥 Descarga Instructores a Excel
            </Button>
          </Col>
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="file"
                accept=".csv"
                onChange={this.handleFileChange}
                disabled={isUploading}
              />
              <Button
                onClick={this.handleFileUpload}
                disabled={isUploading}
                color="primary"
              >
                {isUploading ? (
                  <>
                    <Spinner size="sm" /> Subiendo...
                  </>
                ) : (
                  '📤 Upload CSV'
                )}
              </Button>
            </div>
          </Col>
        </Row>

        {/* Status Summary Alert */}
        {hasInstructors && (
          <Alert color="info" className="mb-3">
            <strong>📊 Resumen de Instructores:</strong>
            <ul className="mb-0 mt-2">
              <li>
                Total: <strong>{statusSummary.total}</strong>
              </li>
              <li>
                Pendientes: <strong>{statusSummary.pending}</strong>
              </li>
              <li>
                Confirmados: <strong className="text-success">{statusSummary.confirmed}</strong>
              </li>
              <li>
                Rechazados: <strong className="text-danger">{statusSummary.declined}</strong>
              </li>
            </ul>
            {!hasPendingInstructors && statusSummary.confirmed > 0 && (
              <div className="mt-2 text-success">
                ✓ Todos los instructores han respondido
              </div>
            )}
          </Alert>
        )}

        <Table responsive hover striped bordered>
          <thead>
            <tr className="table-danger">
              <th>Instructor</th>
              <th>Partner que Asigna</th>
              <th>Horas x Semana</th>
              <th>Precio x Hora</th>
              <th>Costo x Hora</th>
              {/* Moneda removed - inherited from project */}
              <th>Numero de Semanas</th>
              <th>Observaciones</th>
              <th>Fecha de Registro</th>
              <th>Estatus</th>
              <th>Fecha Confirmación</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!instructores || instructores.length <= 0 ? (
              <tr>
                <td colSpan="12" align="center">
                  <b>Ups, todavia no hay instructores!!</b>
                </td>
              </tr>
            ) : (
              instructores.map((instructor) => {
                const precioHora =
                  instructor?.tarifa_hora ?? instructor?.tarifa_hora ?? 0;
                const status = (
                  instructor.confirmation_status || "pending"
                ).toLowerCase();
                const badge =
                  status === "confirmed"
                    ? { className: "badge bg-success", label: "✓ Confirmado" }
                    : status === "declined"
                    ? { className: "badge bg-danger", label: "✗ Rechazado" }
                    : { className: "badge bg-secondary", label: "⏳ Pendiente" };

                return (
                  <tr key={instructor.pk}>
                    <td>{instructor.instructor}</td>
                    <td>{instructor.miembro_que_asigna}</td>
                    <td>{num(instructor.horas)}</td>
                    <td>{fmt2(precioHora)}</td>
                    <td>{fmt2(instructor.costo_hora)}</td>
                    {/* Moneda removed - inherited from project */}
                    <td>{num(instructor.semanas)}</td>
                    <td>{instructor.observaciones}</td>
                    <td>
                      {instructor.registration_date
                        ? Moment(instructor.registration_date).format(
                            "MMM/DD/yy"
                          )
                        : ""}
                    </td>

                    {/* Status + timestamp */}
                    <td>
                      <span className={badge.className}>{badge.label}</span>
                    </td>
                    <td>
                      {instructor.confirmed_at
                        ? Moment(instructor.confirmed_at).format(
                            "MMM/DD/yy HH:mm"
                          )
                        : "—"}
                    </td>

                    <td align="center">
                      <ProyectoIngresoModal
                        create={false}
                        instructor={instructor}
                        resetState={this.props.resetState}
                        proyectoMoneda={this.props.selectedProyecto?.moneda || 'USD'}
                        selectedProyecto={this.props.selectedProyecto}
                      />
                      &nbsp;&nbsp;
                      <ConfirmInstructorRemovalModal
                        pk={instructor.pk}
                        resetState={this.props.resetState}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>

        <div className="d-flex align-items-center gap-2">
          <Button
            color={hasPendingInstructors ? "success" : "secondary"}
            onClick={this.sendEmail}
            disabled={loadingEmail || !hasInstructors || !hasPendingInstructors}
          >
            {loadingEmail
              ? "Enviando..."
              : hasPendingInstructors
              ? `📧 Enviar correo a ${statusSummary.pending} instructor(es) pendiente(s)`
              : "✓ Todos confirmados"}
          </Button>

          {!hasPendingInstructors && hasInstructors && statusSummary.confirmed > 0 && (
            <span className="text-success">
              ✓ No hay instructores pendientes de confirmar
            </span>
          )}
        </div>
      </div>
    );
  }
}

export default ProyectoDetailInstructorList;